/**
 * @fileoverview Cycle Page
 * @description Scenarios belonging to one test cycle.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CalendarRange,
  Clock,
  Layers,
  ListChecks,
  MoreVertical,
  Pencil,
  Play,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  DeltaIndicator,
  EmptyState,
  EvaluationBreadcrumbs,
  MissingRecord,
  ScoreChip,
  SummaryMetric,
} from '../components/common';
import { NameDialog } from '../components/NameDialog';
import {
  createScenario,
  cycleStats,
  deleteScenario,
  scenarioStats,
  updateScenario,
} from '../utils/evaluationStore';
import { bandForScore } from '../utils/evaluationView';

/**
 * Render one scenario row.
 * @param {Object} props - Scenario record and row actions.
 * @returns {JSX.Element} Scenario card.
 */
function ScenarioCard({ scenario, onOpen, onEdit, onDelete }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const stats = scenarioStats(scenario);

  return (
    <Paper className="aier-scenario-card" elevation={0}>
      <Box
        className="aier-scenario-body"
        role="button"
        tabIndex={0}
        onClick={() => onOpen(scenario)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(scenario);
          }
        }}
      >
        <Box className="aier-scenario-main">
          <Typography className="aier-scenario-name">{scenario.name}</Typography>
          <Typography className="aier-scenario-description">
            {scenario.description || 'No description'}
          </Typography>
          <Stack direction="row" spacing={0.75} mt={1.25} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" icon={<Play size={12} />} label={`${stats.runCount} runs`} />
            {stats.latestRun && (
              <Chip
                size="small"
                variant="outlined"
                label={`Latest run ${new Date(stats.latestRun.importedAt).toLocaleDateString()}`}
              />
            )}
            {stats.belowTarget > 0 && (
              <Chip size="small" variant="outlined" color="error" label={`${stats.belowTarget} below target`} />
            )}
          </Stack>
        </Box>

        <Stack className="aier-scenario-score" spacing={0.75} alignItems="flex-end">
          {stats.latestScore === null ? (
            <Typography className="aier-metric-caption">No runs yet</Typography>
          ) : (
            <>
              <ScoreChip score={stats.latestScore} band={bandForScore(stats.latestScore)} />
              <DeltaIndicator delta={stats.delta} />
            </>
          )}
        </Stack>
      </Box>

      <IconButton
        size="small"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        aria-label={`Actions for ${scenario.name}`}
      >
        <MoreVertical size={16} />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); onEdit(scenario); }}>
          <Pencil size={14} style={{ marginRight: 8 }} /> Rename
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(scenario); }} sx={{ color: '#C81E3A' }}>
          <Trash2 size={14} style={{ marginRight: 8 }} /> Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
}

/**
 * Render the scenarios inside a test cycle.
 * @param {Object} props - Workspace projects and change handler.
 * @returns {JSX.Element} Cycle page.
 */
export function CyclePage({ projects, onProjectsChange }) {
  const { projectId, cycleId } = useParams();
  const navigate = useNavigate();
  const [dialogState, setDialogState] = useState(null);

  const project = projects.find((item) => item.id === projectId);
  const cycle = project?.cycles.find((item) => item.id === cycleId);

  if (!project || !cycle) {
    return <MissingRecord icon={CalendarRange} message="This test cycle no longer exists." />;
  }

  const stats = cycleStats(cycle);

  /**
   * Persist the dialog result as a new or renamed scenario.
   * @param {{name: string, description: string}} values - Submitted values.
   * @returns {void}
   */
  const handleDialogSubmit = (values) => {
    onProjectsChange(dialogState.scenario
      ? updateScenario(project.id, cycle.id, dialogState.scenario.id, values)
      : createScenario(project.id, cycle.id, values));
    setDialogState(null);
  };

  return (
    <>
      <EvaluationBreadcrumbs
        trail={[
          { label: project.name, to: `/ai-evaluation-results/${project.id}` },
          { label: cycle.name },
        ]}
      />

      <Box className="aier-header">
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box className="aier-cycle-icon aier-cycle-icon--large">
            <CalendarRange size={22} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1">{cycle.name}</Typography>
            <Typography color="text.secondary">
              {cycle.description || 'Scenarios evaluated in this test cycle.'}
            </Typography>
          </Box>
        </Stack>
        {cycle.scenarios.length > 0 && (
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => setDialogState({ scenario: null })}
          >
            New scenario
          </Button>
        )}
      </Box>

      {cycle.scenarios.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="Add your first scenario"
          message="A scenario is one thing you evaluate repeatedly — a capability, policy area, or test suite. Each run is kept in its history."
        >
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => setDialogState({ scenario: null })}
          >
            New scenario
          </Button>
        </EmptyState>
      ) : (
        <>
          <Box className="aier-summary-grid aier-summary-grid--three" aria-label="Test cycle summary">
            <SummaryMetric
              label="Scenarios"
              value={stats.scenarioCount}
              caption="In this cycle"
              icon={Layers}
              color="#01426A"
            />
            <SummaryMetric
              label="Runs"
              value={stats.runCount}
              caption="Across all scenarios"
              icon={Play}
              color="#0097A9"
            />
            <SummaryMetric
              label="Last activity"
              value={stats.lastActivity ? new Date(stats.lastActivity).toLocaleDateString() : '—'}
              caption="Most recent run recorded"
              icon={Clock}
              color="#008653"
            />
          </Box>

          <Stack spacing={1.5}>
            {cycle.scenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.id}
                scenario={scenario}
                onOpen={(target) => navigate(target.id)}
                onEdit={(target) => setDialogState({ scenario: target })}
                onDelete={(target) => onProjectsChange(deleteScenario(project.id, cycle.id, target.id))}
              />
            ))}
          </Stack>
        </>
      )}

      <NameDialog
        open={Boolean(dialogState)}
        title={dialogState?.scenario ? 'Rename scenario' : 'New scenario'}
        nameLabel="Scenario name"
        initialValues={dialogState?.scenario}
        onCancel={() => setDialogState(null)}
        onSubmit={handleDialogSubmit}
      />
    </>
  );
}

export default CyclePage;
